import { type ReactNode } from 'react'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { Alert, EmptyCell, Typography } from 'shared/ui'
import { ModalInfoBlockWithTitle } from 'shared/ui/Modal/ui/ModalInfoBlockWithTitle/ModalInfoBlockWithTitle'
import { type IModalInfoRowProps } from 'shared/ui/Modal/ui/ModalInfoRow/ModalInfoRow'
import type { ComplianceServiceCampaign } from 'entities/Compliance/model'
import { ModalHeader } from 'pages/settings/pages/compliance/ui/ModalHeader/ModalHeader'
import styles from './styles.module.scss'
import { UseCaseModalTabs } from '../UseCaseModalTabs/UseCaseModalTabs'

type IUseCaseModalContentProps = {
  serviceCampaign: ComplianceServiceCampaign
  statusData: ReactNode
  updateModalAction: () => void
  learnMoreModalAction: () => void
  openImageModal: (urls: string, name: string) => void
}

export const UseCaseModalContent = observer(
  ({
    serviceCampaign,
    statusData,
    updateModalAction,
    learnMoreModalAction,
    openImageModal,
  }: IUseCaseModalContentProps) => {
    const { registrationError, data } = serviceCampaign
    const { useCaseData, sampleMessagesData } = data || {}

    const lines: IModalInfoRowProps[] = [
      {
        label: 'Use case',
        value: useCaseData?.useCaseTitle || '',
      },
      {
        label: 'Description',
        value: useCaseData?.description || '',
      },
      {
        label: 'Sample message 1',
        value: sampleMessagesData?.sampleMessage1 || '',
      },
      {
        label: 'Sample message 2',
        value: sampleMessagesData?.sampleMessage2 || '',
      },
      {
        label: 'Privacy Policy URL',
        value: sampleMessagesData?.privacyPolicyUrl ? (
          <a href={sampleMessagesData.privacyPolicyUrl} target='_blank' rel='noreferrer'>
            {sampleMessagesData.privacyPolicyUrl}
          </a>
        ) : (
          <EmptyCell />
        ),
      },
      {
        label: 'Terms and Conditions URL',
        value: sampleMessagesData?.termsUrl ? (
          <a href={sampleMessagesData.termsUrl} target='_blank' rel='noreferrer'>
            {sampleMessagesData.termsUrl}
          </a>
        ) : (
          <EmptyCell />
        ),
      },
    ]

    return (
      <div className={styles.wrap}>
        <ModalHeader header={useCaseData?.useCaseTitle || ''} statusData={statusData} />

        <div
          className={classNames({
            [styles.topBorder]: !registrationError,
          })}
        >
          {registrationError && (
            <Alert
              item={{
                type: 'error',
                desc: registrationError.message,
                title: 'Your use case campaign application was rejected.',
              }}
              actions={[
                {
                  type: 'primary',
                  variant: 'button',
                  text: 'Update',
                  onClick: updateModalAction,
                },
                {
                  type: 'secondary',
                  variant: 'button',
                  text: 'Learn more',
                  onClick: learnMoreModalAction,
                },
              ]}
            />
          )}
          <div className={styles.divider} />

          <ModalInfoBlockWithTitle title='General info' lines={lines} />

          {!!serviceCampaign.optInData?.length && (
            <div className={styles.optInMethod}>
              <Typography
                ariaLabel='OptInMethod'
                variant='body-rg-medium'
                className={classNames(styles.title, styles.optInMethodTitle)}
              >
                Opt-in method
              </Typography>

              <UseCaseModalTabs
                optInData={serviceCampaign.optInData}
                getOptInDataByMethod={serviceCampaign.getOptInDataByMethod}
                openImageModal={openImageModal}
              />
            </div>
          )}
        </div>
      </div>
    )
  }
)
